/* ME → BRAND (her call, 2026-10-12): who she is TODAY, visually — business
 * name, logo, and her colours in TWO LISTS (her design, same day):
 *   · YOUR COLOURS — what she has: name, colour, and what goes ON it;
 *   · WHERE THEY GO — a short fixed list of jobs, each one of her colours.
 * A colour can do many jobs; a job has exactly one colour, so no tool guesses.
 * Every tool wears these (utils/brand.js `brandSkin`). ME WINS — her locked
 * Accelerator palette only fills a job left empty here.
 *
 * Shaped like Me → Skills and Voice: nothing to press — a colour saves when
 * she leaves it, a job when she picks it. The read-out turns amber when a save
 * fails (rule 1), because "Saved" and silence look identical.
 *
 * WHEN ME AND THE ACCELERATOR DISAGREE nothing changes by itself (her rule):
 * the difference is NAMED, both swatches side by side, and she chooses.
 */
import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, Info, Palette, Plus, Settings2, X } from "lucide-react";
import { MediaPickButton, useCrmConnected } from "./momentum/mediaPicker";
import { loadGoogleFont } from "../utils/markdown";
import { useSession } from "@clerk/clerk-react";
import {
  BRAND_JOBS, BRAND_ROLES, MAX_COLOURS, acceleratorBrand, brandConflict, effectiveJobs, jobColour, jobContrast, newColourId, normHex, ratio, readBrand, autoOn, onOf, SOFT_WHITE, CHARCOAL, FONT_SLOTS, acceleratorFonts, fontConflict, LOGO_SLOTS,
} from "../utils/brand";

const label = { display: "block", fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 };
const field = { width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 8, fontSize: 14, fontFamily: "inherit", border: "1px solid var(--border-strong, var(--border))", background: "var(--bg-surface)", color: "var(--text-primary)" };
const card = { background: "var(--bg-surface)", border: "1px solid var(--border-subtle, var(--border))", borderRadius: 12, padding: 18 };
const quietBtn = { padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: "inherit", cursor: "pointer", border: "1px solid var(--accent)", background: "var(--accent-quiet)", color: "var(--accent)" };
const mono = { fontFamily: "ui-monospace, Menlo, monospace", textTransform: "uppercase" };

/* A FOLDING SECTION (her ask, 2026-10-12: "make the colors and fonts
   collapsible"). The header is the whole target; open or closed is
   remembered per browser — a convenience, so unreadable storage just means
   the default (open while empty, closed once filled). */
const openKey = (id) => `brill.brand.open.${id}`;
function Section({ id, title, sub, filled, note = null, children }) {
  /* HER choice once she has made one; until then it FOLLOWS `filled` — the
     screen first draws before her brand has loaded, and a default read at
     that moment opened a full section (seen 2026-10-12). */
  const [choice, setChoice] = useState(() => {
    try { const v = window.localStorage.getItem(openKey(id)); return v === null ? null : v === "1"; } catch { return null; }
  });
  const open = choice ?? !filled;
  const setOpen = (v) => { setChoice(v); try { window.localStorage.setItem(openKey(id), v ? "1" : "0"); } catch { /* a convenience */ } };
  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={() => setOpen(!open)} aria-expanded={open}
          style={{ flex: 1, display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", textAlign: "left", color: "var(--text-primary)" }}>
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span style={{ flex: 1 }}>
            <span style={{ display: "block", fontSize: 15, fontWeight: 700 }}>{title}</span>
            {sub && <span style={{ display: "block", fontSize: 12.5, color: "var(--text-secondary)", fontWeight: 400 }}>{sub}</span>}
          </span>
        </button>
        {note}
      </div>
      {open && <div style={{ marginTop: 8 }}>{children}</div>}
    </div>
  );
}

/* THE ACCELERATOR NOTE AS A SMALL ⓘ (her ask, 2026-10-12: "like a little i
   in salmon until you've read it, then collapses"). Salmon while unread;
   opening shows it; closing marks it read and the ⓘ goes quiet. A NEW
   difference (another `signature`) is unread again. Read-state is per
   browser — it only decides the color of the ⓘ, never what is shown. */
const readKey = (sig) => `brill.brand.read.${sig}`;
function AccNote({ signature, label, children }) {
  const [open, setOpen] = useState(false);
  const [read, setRead] = useState(() => { try { return window.localStorage.getItem(readKey(signature)) === "1"; } catch { return false; } });
  useEffect(() => { try { setRead(window.localStorage.getItem(readKey(signature)) === "1"); } catch { setRead(false); } }, [signature]);
  const close = () => { setOpen(false); setRead(true); try { window.localStorage.setItem(readKey(signature), "1"); } catch { /* a convenience */ } };
  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      <button onClick={() => (open ? close() : setOpen(true))} aria-expanded={open} aria-label={label} title={label} data-read={read ? "yes" : "no"}
        style={{ display: "inline-flex", padding: 4, border: "none", background: "none", cursor: "pointer", color: read ? "var(--text-muted)" : "var(--danger-text)" }}>
        <Info size={18} />
      </button>
      {open && (
        <div role="note" aria-label={label}
          style={{ position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: 15, width: "min(320px, 80vw)", background: "var(--bg-surface)", border: "1px solid var(--border-strong, var(--border))", borderRadius: 10, padding: 12, boxShadow: "0 12px 32px rgba(0,0,0,0.3)" }}>
          {children}
          <button onClick={close} style={{ display: "block", marginTop: 10, border: "none", background: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, color: "var(--text-secondary)" }}>Got it</button>
        </div>
      )}
    </span>
  );
}

/* THE SWATCH BOARD (her ask, 2026-10-12: "is there any way to show the
   colors you picked in the accelerator … I would have to go back to the
   accelerator, find that value to pick it"). A color square opens HER colors
   and her Accelerator palette first — one tap uses one — and "Any color…"
   behind them opens the browser's full picker as before. */
function BoardPicker({ value, label, size = 44, dashed = false, boards = [], onPick }) {
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const native = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", away); document.removeEventListener("keydown", esc); };
  }, [open]);
  const pick = (hex) => { setOpen(false); onPick(hex); };
  /* "Any color…": the browser's picker fires `change` once, when its window
     closes — that is the pick. Dragging inside it fires `input` and is ignored.

     LISTENING WHETHER OR NOT THE BOARD IS OPEN (her report, 2026-10-15: "I
     select text on it and pick a new color, it defaults back to the soft
     white automatic"). She closes Chrome's color window by clicking the page;
     that click reached the `away` listener above and closed the board FIRST,
     which unmounted this input — so Chrome's `change` landed on nothing and
     the pick was lost. The input now lives outside the board and is always
     there to receive it. */
  const pickRef = useRef(onPick);
  pickRef.current = onPick;
  useEffect(() => {
    const el = native.current;
    if (!el) return undefined;
    const done = () => { const h = normHex(el.value); if (h) { setOpen(false); pickRef.current(h); } };
    el.addEventListener("change", done);
    return () => el.removeEventListener("change", done);
  }, []);
  const openNative = () => {
    const el = native.current;
    if (!el) return;
    el.value = value ? `#${value}` : "#ffffff";   // start from what is there now
    if (el.showPicker) el.showPicker(); else el.click();
  };
  const shown = boards.filter((b) => b.colors.length);
  return (
    <span ref={wrap} style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <button type="button" aria-label={label} title={label} aria-expanded={open} onClick={() => setOpen((v) => !v)}
        style={{ width: size, height: size, padding: 0, borderRadius: size > 32 ? 8 : 6, cursor: "pointer", background: value ? `#${value}` : "transparent", border: `1px ${dashed ? "dashed" : "solid"} var(--border-strong, var(--border))`, opacity: dashed ? 0.5 : 1 }} />
      {open && (
        <div role="dialog" aria-label={`${label} — your colors`}
          style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 20, width: 236, background: "var(--bg-surface)", border: "1px solid var(--border-strong, var(--border))", borderRadius: 10, padding: 10, boxShadow: "0 12px 32px rgba(0,0,0,0.3)" }}>
          {shown.map((b) => (
            <div key={b.title} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, color: "var(--text-secondary)", marginBottom: 6 }}>{b.title}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {b.colors.map((c) => (
                  <button key={`${b.title}-${c.hex}`} type="button" onClick={() => pick(c.hex)} aria-label={`${c.name || "Color"} #${c.hex}`} title={`${c.name ? `${c.name} · ` : ""}#${c.hex}`}
                    style={{ width: 30, height: 30, padding: 0, borderRadius: 6, cursor: "pointer", background: `#${c.hex}`, border: c.hex === value ? "2px solid var(--accent)" : "1px solid var(--border-strong, var(--border))" }} />
                ))}
              </div>
            </div>
          ))}
          <button type="button" onClick={openNative}
            style={{ border: "none", background: "none", padding: "2px 0", cursor: "pointer", fontFamily: "inherit", fontSize: 12.5, fontWeight: 600, color: "var(--accent)" }}>
            Any color…
          </button>
        </div>
      )}
      {/* The browser's own picker, opened by "Any color…"; its pick lands
          when its window closes — after the board may already be shut. */}
      <input ref={native} type="color" aria-hidden="true" tabIndex={-1} defaultValue={value ? `#${value}` : "#ffffff"}
        style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none", left: 0, bottom: 0 }} />
    </span>
  );
}

/* One logo slot: the picture on the background it is for, a link box, and
   Pick (her pictures in Brill; GHL and Canva too for admin). Saves on leaving the box. */
function LogoSlot({ slot, value, bg, onCommit }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  const commit = (v) => {
    const url = String(v || "").trim();
    if (url && !/^https?:\/\//i.test(url)) return;
    if (url !== (value || "")) onCommit(url);
  };
  return (
    <div style={{ border: "1px solid var(--border-subtle, var(--border))", borderRadius: 10, overflow: "hidden" }}>
      <div aria-label={`${slot.label} preview`} style={{ height: 76, background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 10 }}>
        {draft
          ? <img src={draft} alt={slot.label} style={{ maxHeight: 54, maxWidth: "100%" }} />
          : <span style={{ fontSize: 11.5, color: "rgba(128,128,128,0.9)" }}>No logo yet</span>}
      </div>
      <div style={{ padding: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>{slot.label}</div>
        <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginBottom: 8 }}>{slot.hint}</div>
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          <input type="url" aria-label={`${slot.label} link`} value={draft} placeholder="https://…"
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => commit(draft)}
            style={{ ...field, flex: 1, minWidth: 120, padding: "7px 9px", fontSize: 12.5 }} />
          {<MediaPickButton remember="brand" label="Pick" onPick={(url) => { setDraft(url); commit(url); }} />}
        </div>
      </div>
    </div>
  );
}

function Swatch({ hex, size = 22, title }) {
  return <span title={title || (hex ? `#${hex}` : "Not set")} style={{ display: "inline-block", width: size, height: size, borderRadius: 6, flexShrink: 0, border: "1px solid var(--border-strong, var(--border))", background: hex ? `#${hex}` : "repeating-linear-gradient(45deg, transparent 0 4px, var(--border-subtle, #ddd) 4px 5px)" }} />;
}

/* The ratio, green when it passes and amber when it doesn't — said in words
   as well as colour, because a colour alone is the thing being judged. */
function Ratio({ value, min = 4.5 }) {
  if (value === null || value === undefined) return null;
  const ok = value >= min;
  return <span style={{ fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", color: ok ? "var(--accent)" : "var(--warn-text)" }}>{value} : 1 {ok ? "✓" : "· hard to read"}</span>;
}

export function BrandPanel({ userSettings = {}, saveSettings, onSaveBrand, voiceOutputs = {}, isMobile }) {
  const crm = useCrmConnected();
  const [brand, setBrand] = useState(() => readBrand(userSettings.brand));
  const [typed, setTyped] = useState({});           // a hex box or picker mid-change, by colour id
  const [bad, setBad] = useState(null);             // the colour whose typed code isn't a colour
  const [name, setName] = useState(userSettings.businessName || "");
  const [logo, setLogo] = useState(userSettings.logoUrl || "");
  const [status, setStatus] = useState("idle");     // idle | saving | saved | failed
  const [fineTune, setFineTune] = useState(false);  // the jobs, behind a toggle (her design)

  /* Settings arrive after the screen mounts (a slow read): take them, unless
     she has already started changing things. */
  const touched = useRef(false);
  useEffect(() => { if (!touched.current) setBrand(readBrand(userSettings.brand)); }, [userSettings.brand]);
  useEffect(() => { if (!touched.current) setName(userSettings.businessName || ""); }, [userSettings.businessName]);
  useEffect(() => { if (!touched.current) setLogo(userSettings.logoUrl || ""); }, [userSettings.logoUrl]);

  const save = async (next) => {
    touched.current = true;
    setBrand(next);
    setStatus("saving");
    const ok = await onSaveBrand(next);
    setStatus(ok ? "saved" : "failed");
  };
  const patchColour = (id, patch) => save({ ...brand, palette: brand.palette.map((c) => (c.id === id ? { ...c, ...patch } : c)) });
  const setJob = (job, id) => {
    const jobs = { ...brand.jobs };
    if (id) jobs[job] = id; else delete jobs[job];
    save({ ...brand, jobs });
  };
  const addColour = () => {
    const c = { id: newColourId(), name: "", hex: "", on: null, role: brand.palette.some((x) => x.role === "primary") ? "" : "primary" };
    touched.current = true;
    setBrand((b) => ({ ...b, palette: [...b.palette, c] }));
    setTyped((t) => ({ ...t, [c.id]: "" }));
  };
  const removeColour = (id) => {
    const jobs = Object.fromEntries(Object.entries(brand.jobs).filter(([, v]) => v !== id));
    save({ ...brand, palette: brand.palette.filter((c) => c.id !== id), jobs });
  };
  /* A new colour exists only once it has a code: until then it is a row
     she is still filling, and saving it would store nothing. */
  const commitHex = (id) => {
    const v = typed[id];
    if (v === undefined) return;
    setTyped((t) => { const n = { ...t }; delete n[id]; return n; });
    const h = normHex(v);
    if (!String(v).trim()) { setBad(null); if (!brand.palette.find((c) => c.id === id)?.hex) setBrand((b) => ({ ...b, palette: b.palette.filter((c) => c.id !== id) })); return; }
    if (!h) { setBad(id); setTyped((t) => ({ ...t, [id]: v })); return; }
    setBad(null);
    applyHex(id, h);
  };
  const applyHex = (id, h) => {
    const c = brand.palette.find((x) => x.id === id);
    if (!c || c.hex === h) return;
    /* Its words stay AUTOMATIC (Soft White or Charcoal) unless she picked. */
    save({ ...brand, palette: brand.palette.map((x) => (x.id === id ? { ...x, hex: h } : x)) });
  };
  /* Name and logo live in their own columns (0028), through saveSettings. */
  const saveField = (key, value) => {
    touched.current = true;
    if ((userSettings[key] || "") === value) return;
    saveSettings({ [key]: value });
    setStatus("saved");
  };

  const theirs = acceleratorBrand(voiceOutputs);
  const conflict = brandConflict(brand, voiceOutputs);
  const empty = brand.palette.length === 0;
  const named = (c) => c.name || `#${c.hex}`;
  const saved = brand.palette.filter((c) => c.hex);

  /* Take the Accelerator's colour for the jobs that disagree: reuse it if she
     already has it, else add it under its Accelerator name. */
  const useTheirs = () => {
    const palette = [...brand.palette];
    const jobs = { ...brand.jobs };
    for (const c of conflict) {
      let mine = palette.find((x) => x.hex === c.accelerator.hex);
      if (!mine) { mine = { id: newColourId(), name: c.accelerator.name, hex: c.accelerator.hex, on: null }; palette.push(mine); }
      jobs[c.job] = mine.id;
    }
    save({ ...brand, palette, jobs });
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: "auto", background: "var(--bg-primary)", padding: isMobile ? "16px 16px 100px" : "32px 32px 48px" }}>
      <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Palette size={18} color="var(--accent)" />
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "var(--text-primary)" }}>Brand</h2>
            <p role="status" style={{ margin: "2px 0 0", fontSize: 12.5, color: status === "failed" ? "var(--warn-text)" : "var(--text-secondary)" }}>
              {status === "failed" ? "That didn't save. Check your connection and try again."
                : status === "saving" ? "Saving…"
                : status === "saved" ? "✓ Saved"
                : "Your business as it looks today. Every deck, email and brief uses these."}
            </p>
          </div>
        </div>

        {/* ── Name and logo ─────────────────────────────────────────────── */}
        <div style={card}>
          <label style={label} htmlFor="brand-name">Business name</label>
          <input id="brand-name" value={name} onChange={(e) => setName(e.target.value)} onBlur={() => saveField("businessName", name.trim())}
            placeholder="What your business is called" style={{ ...field, marginBottom: 16 }} />
          {/* LOGOS SIDE BY SIDE (her call, 2026-10-13): each shown on the
              background it is for, so a white logo on a white tile — the
              thing she caught on her GHL board — is obvious here first. */}
          <div style={label}>Logos</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            {LOGO_SLOTS.map((s) => (
              <LogoSlot key={s.id} slot={s}
                value={s.id === "light" ? logo : brand.logos[s.id] || ""}
                bg={s.id === "dark" ? (jobColour(brand, "darkBg")?.hex ? `#${jobColour(brand, "darkBg").hex}` : "var(--chrome-bg)") : (jobColour(brand, "lightBg")?.hex ? `#${jobColour(brand, "lightBg").hex}` : "white")}
                /* The slot holds its own draft and reports only on commit —
                   updating the saved value while she typed made leaving the
                   box look like no change, and the light logo never saved
                   (caught by test, 2026-10-13). */
                onCommit={(url) => {
                  if (s.id === "light") { setLogo(url); return saveField("logoUrl", url); }
                  const logos = { ...brand.logos };
                  if (url) logos[s.id] = url; else delete logos[s.id];
                  save({ ...brand, logos });
                }} />
            ))}
          </div>
        </div>

        {/* ── 1. YOUR COLOURS ────────────────────────────────────────────── */}
        <Section id="colors" title="Your colors" sub="Every color in your brand, and the color of the words that go on it." filled={saved.length > 0}
          note={(empty && theirs.palette.length > 0) || conflict.length > 0 ? (
            <AccNote label={conflict.length ? "Your Accelerator palette is different" : "Start from your Accelerator palette"}
              signature={conflict.length ? `c-${conflict.map((c) => `${c.job}${c.mine.hex}${c.accelerator.hex}`).join("")}` : `o-${theirs.palette.map((c) => c.hex).join("")}`}>
              {conflict.length > 0 ? (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Your Accelerator palette is different</div>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: 8 }}>Everything uses your colors here until you choose. Your locked My Voice &amp; Branding says:</div>
                  {conflict.map((c) => (
                    <div key={c.job} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "var(--text-primary)", marginBottom: 5, flexWrap: "wrap" }}>
                      <span style={{ minWidth: 110 }}>{c.label}</span>
                      <Swatch hex={c.mine.hex} size={18} /> <span style={{ color: "var(--text-secondary)" }}>here ·</span>
                      <Swatch hex={c.accelerator.hex} size={18} /> <span style={{ color: "var(--text-secondary)" }}>Accelerator</span>
                    </div>
                  ))}
                  <button style={{ ...quietBtn, marginTop: 6 }} onClick={useTheirs}>Use the Accelerator's instead</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Start from your Accelerator palette?</div>
                  <div style={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
                    {theirs.fromNames
                      ? "Your locked palette names its colors without codes, so Brill matched a color to each name. Use these, then change any that are off."
                      : "Every color you locked, with the jobs Brill read for them."}
                  </div>
                  <div style={{ display: "flex", gap: 5, margin: "8px 0 10px", flexWrap: "wrap" }}>
                    {theirs.palette.map((c) => <Swatch key={c.id} hex={c.hex} size={22} title={`${c.name}: #${c.hex}`} />)}
                  </div>
                  <button style={quietBtn} onClick={() => save({ ...theirs, fonts: brand.fonts, logos: brand.logos })}>Use these</button>
                </>
              )}
            </AccNote>
          ) : null}>
          {brand.palette.map((c) => {
            const shownHex = typed[c.id] !== undefined ? typed[c.id] : c.hex ? `#${c.hex}` : "";
            const live = normHex(typed[c.id]) || c.hex;
            const others = saved.filter((x) => x.id !== c.id);
            return (
              <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 0", borderTop: "1px solid var(--border-subtle, var(--border))", flexWrap: "wrap" }}>
                {/* Dragging in the picker fires on every move, so it only
                    TYPES the code; leaving the picker saves it, once. An unset
                    colour looks unset — the picker cannot be blank. */}
                <BoardPicker label={`${c.name || "New color"} picker`} value={live} dashed={!live}
                  boards={[
                    { title: "From your Accelerator", colors: theirs.palette },
                    { title: "Your other colors", colors: others },
                  ]}
                  onPick={(h) => { touched.current = true; setTyped((t) => { const n = { ...t }; delete n[c.id]; return n; }); applyHex(c.id, h); }} />
                <div style={{ flex: 1, minWidth: 150, display: "flex", flexDirection: "column", gap: 6 }}>
                  <input aria-label="Color name" defaultValue={c.name} placeholder="Name it — e.g. Salmon"
                    onBlur={(e) => { const v = e.target.value.trim(); if (c.hex && v !== c.name) patchColour(c.id, { name: v }); else if (!c.hex) setBrand((b) => ({ ...b, palette: b.palette.map((x) => (x.id === c.id ? { ...x, name: v } : x)) })); }}
                    style={{ ...field, padding: "7px 10px", fontWeight: 600 }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <input aria-label={`${c.name || "New color"} code`} value={shownHex} placeholder="#______"
                      onChange={(e) => setTyped((t) => ({ ...t, [c.id]: e.target.value }))}
                      onBlur={() => commitHex(c.id)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      style={{ ...field, ...mono, width: 104, padding: "7px 10px", borderColor: bad === c.id ? "var(--warn-text)" : undefined }} />
                    {/* ROLE — the question people know the answer to. The jobs
                        below the Fine-tune toggle are worked out from it. */}
                    <select aria-label={`Role of ${c.name || "this color"}`} value={c.role || ""}
                      onChange={(e) => (c.hex ? patchColour(c.id, { role: e.target.value }) : setBrand((b) => ({ ...b, palette: b.palette.map((x) => (x.id === c.id ? { ...x, role: e.target.value } : x)) })))}
                      style={{ ...field, width: "auto", padding: "6px 8px", fontSize: 12.5, fontWeight: 600 }}>
                      <option value="">Role…</option>
                      {BRAND_ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                    {c.hex && (
                      <>
                        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>Text on it</span>
                        {/* AUTOMATIC by default (her call): Soft White or
                            Charcoal, whichever reads better. Anything else here
                            is her override, and "Automatic" hands it back. */}
                        {/* THE SQUARE IS A PICKER (her report, 2026-10-12: "I
                            can't hit the button for text on it") — any color for
                            the words, not only the list. Dragging types; leaving
                            the picker saves it once, as her override. */}
                        <BoardPicker label={`Pick the text color on ${c.name || "this color"}`} size={30} value={onOf(c)}
                          boards={[
                            { title: "For words", colors: [{ name: "Soft White", hex: SOFT_WHITE }, { name: "Charcoal", hex: CHARCOAL }] },
                            { title: "Your colors", colors: others },
                            { title: "From your Accelerator", colors: theirs.palette.filter((t) => !saved.some((o) => o.hex === t.hex)) },
                          ]}
                          onPick={(h) => patchColour(c.id, { on: h })} />
                        <select aria-label={`Text on ${c.name || "this color"}`} value={c.on || ""} onChange={(e) => patchColour(c.id, { on: e.target.value || null })}
                          style={{ ...field, width: "auto", padding: "6px 8px", fontSize: 12.5 }}>
                          <option value="">Automatic · {autoOn(c.hex) === SOFT_WHITE ? "Soft White" : "Charcoal"}</option>
                          <option value={SOFT_WHITE}>Soft White</option>
                          <option value={CHARCOAL}>Charcoal</option>
                          {others.filter((o) => o.hex !== SOFT_WHITE && o.hex !== CHARCOAL).map((o) => <option key={o.id} value={o.hex}>{named(o)}</option>)}
                          {c.on && c.on !== SOFT_WHITE && c.on !== CHARCOAL && !others.some((o) => o.hex === c.on) && <option value={c.on}>#{c.on}</option>}
                        </select>
                        <Ratio value={ratio(c.hex, onOf(c))} />
                      </>
                    )}
                  </div>
                  {bad === c.id && <span style={{ fontSize: 11.5, color: "var(--warn-text)" }}>Not a color code — e.g. #007D6E</span>}
                </div>
                <button onClick={() => removeColour(c.id)} aria-label={`Remove ${c.name || "this color"}`} title="Remove this color"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-secondary)", padding: 6, display: "flex", alignSelf: "flex-start" }}>
                  <X size={16} />
                </button>
              </div>
            );
          })}
          {brand.palette.length < MAX_COLOURS && (
            <button onClick={addColour} style={{ ...quietBtn, marginTop: 10, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Plus size={14} /> Add a color
            </button>
          )}
        </Section>

        {/* ── FONTS (2026-10-12) — a heading font and a body font, shown in
            their own lettering. Her Accelerator pair is OFFERED, and a
            difference from it is NAMED, never settled by itself. */}
        <FontsCard fonts={brand.fonts} theirs={acceleratorFonts(voiceOutputs)} conflict={fontConflict(brand, voiceOutputs)}
          onSave={(fonts) => save({ ...brand, fonts })} />

        {/* ── 2. FINE-TUNE — where each color goes, behind a toggle ─────────
            Every job follows her roles until she picks one here (her design,
            2026-10-12): Accent drives the buttons, Primary the headings, her
            darkest and lightest Neutrals the backgrounds. Picking "Follows
            your roles" hands a job back. */}
        {saved.length > 0 && (
          <div style={card}>
            <button onClick={() => setFineTune((v) => !v)} aria-expanded={fineTune}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", fontFamily: "inherit", textAlign: "left", color: "var(--text-primary)" }}>
              <Settings2 size={16} color="var(--accent)" />
              <span style={{ flex: 1 }}>
                <span style={{ display: "block", fontSize: 15, fontWeight: 700 }}>Fine-tune where each color goes</span>
                <span style={{ display: "block", fontSize: 12.5, color: "var(--text-secondary)", fontWeight: 400 }}>Worked out from your roles. Change any one here.</span>
              </span>
              {fineTune ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            </button>
            {fineTune && BRAND_JOBS.map((j) => {
              const now = jobColour(brand, j.id);
              const following = effectiveJobs(brand).derived.includes(j.id) || !now;
              const cr = jobContrast(brand, j.id);
              return (
                <div key={j.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderTop: "1px solid var(--border-subtle, var(--border))", marginTop: j === BRAND_JOBS[0] ? 12 : 0, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                  <div style={{ flex: 1, minWidth: 150 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{j.label}</div>
                    <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>{j.hint}</div>
                  </div>
                  <Swatch hex={now?.hex} size={22} />
                  <select aria-label={j.label} value={following ? "" : now.id} onChange={(e) => setJob(j.id, e.target.value)}
                    style={{ ...field, width: "auto", minWidth: 150, padding: "7px 10px", fontSize: 13 }}>
                    <option value="">{now ? `Follows your roles — ${named(now)}` : theirs.jobs[j.id] ? "Use the Accelerator's" : "Not set"}</option>
                    {saved.map((c) => <option key={c.id} value={c.id}>{named(c)}</option>)}
                  </select>
                  {cr && <Ratio value={cr.ratio} min={j.min} />}
                </div>
              );
            })}
          </div>
        )}

        {/* ── How they sit together ──────────────────────────────────────── */}
        {crm && (saved.length > 0 || brand.fonts.heading || brand.fonts.body) && (
          <BrandToGhl brand={brand} businessName={name} logoUrl={logo} />
        )}

        {saved.length > 0 && <BrandPreview brand={brand} name={name} />}
      </div>
    </div>
  );
}

/* Names to type from — the Google fonts coaches reach for most, plus hers.
   Any Google font works; this is only the list the box suggests. */
const COMMON_FONTS = [
  "Poppins", "Lexend", "Inter", "Montserrat", "Lato", "Open Sans", "Raleway", "Nunito Sans", "DM Sans", "Work Sans",
  "Playfair Display", "Fraunces", "Cormorant Garamond", "Libre Baskerville", "Lora", "Merriweather", "DM Serif Display",
  "Young Serif", "Sora", "Outfit", "Josefin Sans", "Cinzel", "Bodoni Moda", "Source Sans 3", "Karla",
];

function FontsCard({ fonts = {}, theirs = {}, conflict = [], onSave }) {
  const [draft, setDraft] = useState(fonts);
  useEffect(() => { setDraft(fonts); }, [fonts.heading, fonts.body]); // eslint-disable-line
  /* Each name is loaded so the preview is IN that font — the one way to see a
     misspelled name: it stays in the fallback. */
  useEffect(() => { for (const s of FONT_SLOTS) { loadGoogleFont(draft[s.id]); loadGoogleFont(theirs[s.id]); } }, [draft.heading, draft.body, theirs.heading, theirs.body]); // eslint-disable-line
  const commit = (id) => {
    const v = String(draft[id] || "").trim();
    if (v === (fonts[id] || "")) return;
    const next = { ...fonts };
    if (v) next[id] = v; else delete next[id];
    onSave(next);
  };
  const hasTheirs = Boolean(theirs.heading || theirs.body);
  const empty = !fonts.heading && !fonts.body;
  const noteOpen = (empty && hasTheirs) || conflict.length > 0;
  return (
    <Section id="fonts" title="Your fonts" sub="Any Google font, by its name. The preview shows it in its own lettering." filled={!empty}
      note={noteOpen ? (
        <AccNote label={conflict.length ? "Your Accelerator fonts are different" : "Your Accelerator font pair"}
          signature={conflict.length ? `f-${conflict.map((c) => `${c.slot}${c.mine}${c.accelerator}`).join("")}` : `fo-${theirs.heading}${theirs.body}`}>
          {conflict.length > 0 ? (
            <>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)", marginBottom: 6 }}>Your Accelerator fonts are different</div>
              {conflict.map((c) => (
                <div key={c.slot} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12.5, color: "var(--text-secondary)", marginBottom: 6 }}>
                  {c.label}: the Accelerator says <b style={{ color: "var(--text-primary)", fontFamily: `"${c.accelerator}", sans-serif` }}>{c.accelerator}</b>
                  <button style={{ ...quietBtn, padding: "4px 10px", fontSize: 12 }} onClick={() => onSave({ ...fonts, [c.slot]: c.accelerator })}>Use it instead</button>
                </div>
              ))}
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: "var(--text-primary)", marginBottom: 8 }}>
                Your Accelerator pair: <b style={{ fontFamily: `"${theirs.heading}", sans-serif` }}>{theirs.heading || "—"}</b> / <span style={{ fontFamily: `"${theirs.body}", sans-serif` }}>{theirs.body || "—"}</span>
              </div>
              <button style={quietBtn} onClick={() => onSave({ ...theirs })}>Use these</button>
            </>
          )}
        </AccNote>
      ) : null}>
      <datalist id="brand-font-names">
        {[...new Set([theirs.heading, theirs.body, ...COMMON_FONTS].filter(Boolean))].map((f) => <option key={f} value={f} />)}
      </datalist>
      {FONT_SLOTS.map((s) => {
        const name = draft[s.id] || "";
        return (
          <div key={s.id} style={{ padding: "12px 0", borderTop: "1px solid var(--border-subtle, var(--border))" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>{s.label}</div>
                <div style={{ fontSize: 12, color: "var(--text-secondary)" }}>{s.hint}</div>
              </div>
              <input aria-label={s.label} list="brand-font-names" value={name} placeholder={theirs[s.id] ? `${theirs[s.id]} (Accelerator)` : "e.g. Poppins"}
                onChange={(e) => setDraft((d) => ({ ...d, [s.id]: e.target.value }))}
                onBlur={() => commit(s.id)}
                onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                style={{ ...field, width: 200, padding: "8px 10px" }} />
            </div>
            {name && (
              <div aria-label={`${s.label} preview`} style={{ marginTop: 8, fontFamily: `"${name}", sans-serif`, color: "var(--text-primary)", fontSize: s.id === "heading" ? 22 : 15, fontWeight: s.id === "heading" ? 700 : 400, lineHeight: 1.35 }}>
                {s.id === "heading" ? "Build the business that fits your life" : "This is how a paragraph of your words reads, in your own body font."}
              </div>
            )}
          </div>
        );
      })}
    </Section>
  );
}

/* ME → BRAND → A GHL BRAND BOARD (her ask, 2026-10-12). Admin only, like
   every GHL button. One board, "Brill · <business>"; pressing again updates it. */
function BrandToGhl({ brand, businessName, logoUrl }) {
  const { session } = useSession();
  const [state, setState] = useState(null);   // { busy } | { ok, text }
  const send = async () => {
    setState({ busy: true });
    try {
      const token = await session?.getToken();
      const res = await fetch("/api/ghl", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ job: "brand", businessName, palette: brand.palette.filter((c) => c.hex).map((c) => ({ name: c.name, hex: c.hex })), fonts: brand.fonts, logoUrl,
          logos: [["light", "On light", logoUrl], ["dark", "On dark", brand.logos.dark], ["icon", "Icon", brand.logos.icon]].filter(([, , u]) => u).map(([id, label, url]) => ({ id, label, url })) }),
      });
      const r = await res.json().catch(() => ({}));
      setState(r.ok
        ? { ok: true, text: `${r.updated ? "Updated" : "Made"} your GHL brand board “${r.name}” — ${r.colors} color${r.colors === 1 ? "" : "s"}, ${r.fonts} font${r.fonts === 1 ? "" : "s"}${r.logo === "sent" ? ", your logo" : r.logo === "refused" ? ". GHL didn't take the logo from a link — add it on the board in GHL" : ""}. Settings → Brand Boards in GHL.` }
        : { ok: false, text: `It didn't reach GHL: ${r.error || "try again in a moment."}` });
    } catch {
      setState({ ok: false, text: "Couldn't reach GHL just then. Try again in a moment." });
    }
  };
  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text-primary)" }}>Your brand in GHL</div>
          <div style={{ fontSize: 12.5, color: "var(--text-secondary)" }}>Your colors, fonts and logo as a GHL brand board, so your funnels and emails use them too.</div>
        </div>
        <button onClick={send} disabled={state?.busy} style={{ ...quietBtn, opacity: state?.busy ? 0.6 : 1 }}>
          {state?.busy ? "Sending…" : "Send to GHL"}
        </button>
      </div>
      {state?.text && <div role="status" style={{ marginTop: 10, fontSize: 12.5, lineHeight: 1.55, color: state.ok ? "var(--text-secondary)" : "var(--warn-text)" }}>{state.text}</div>}
    </div>
  );
}

function BrandPreview({ brand, name }) {
  const job = (id) => jobColour(brand, id);
  const hx = (c, fb) => (c?.hex ? `#${c.hex}` : fb);
  const btn = (c) => c && (
    <span style={{ display: "inline-block", padding: "10px 18px", borderRadius: 8, fontWeight: 700, fontSize: 14, color: `#${onOf(c)}`, background: `#${c.hex}`, marginRight: 8 }}>
      {c.id === job("button")?.id ? "Book a call" : "Learn more"}
    </span>
  );
  return (
    <div aria-label="Preview" style={{ borderRadius: 12, overflow: "hidden", border: "1px solid var(--border-subtle, var(--border))" }}>
      <div style={{ background: hx(job("lightBg"), "#fff"), padding: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: hx(job("headings"), "#2C2C2C"), marginBottom: 6 }}>{name || "Your business"}</div>
        <div style={{ fontSize: 14, color: hx(job("text"), "#2C2C2C"), lineHeight: 1.6, marginBottom: 14 }}>
          This is how your words sit on your page, with <span style={{ color: hx(job("links"), "#2C2C2C"), textDecoration: "underline" }}>a link</span>
          {job("highlight") && <> and <span style={{ borderBottom: `3px solid ${hx(job("highlight"))}` }}>a highlight</span></>}.
        </div>
        {btn(job("button"))}{btn(job("button2"))}
      </div>
      {job("darkBg") && (
        <div style={{ background: hx(job("darkBg")), padding: "14px 20px", fontSize: 13.5, color: hx(job("textOnDark"), "#fff") }}>A dark section</div>
      )}
    </div>
  );
}
